import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { reviewAPI } from '../services/api';
import { validateCategory } from '../utils/validCategories';

const AdminDashboard = () => {
  const { user, authToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [activeTab, setActiveTab] = useState('qa-feedback');
  const [showModal, setShowModal] = useState(false);

  // Bulk import state
  const [selectedEssays, setSelectedEssays] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSettings, setBulkSettings] = useState({
    teacherId: '',
    useAutoMapping: true
  });
  const [qaFeedbackEssays, setQAFeedbackEssays] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);

  // Re-grading state (only for training data)
  const [regrading, setRegrading] = useState({});
  const [selectedForRegrade, setSelectedForRegrade] = useState([]);
  const [showAllTraining, setShowAllTraining] = useState(false);
  const [reassigning, setReassigning] = useState({});
  const [showNonQAOnly, setShowNonQAOnly] = useState(false);
  const [trainingSearch, setTrainingSearch] = useState('');

  // Import valid categories from centralized validation
  const { getAllValidCategories } = require('../utils/validCategories');
  const [validCategories, setValidCategories] = useState(getAllValidCategories());
  const [trainingSubjects, setTrainingSubjects] = useState({
    // GCSE
    "GCSE - English Language (AQA)": [
      "Paper 1, Section B.",
      "Paper 2, Section B."
    ],
    "GCSE - Spanish (AQA)": [
      "Paper 4, Q3",
      "Paper 4, Q2",
      "Paper 4, Q1"
    ],
    "GCSE - French (AQA)": [
      "Paper 4, Q3",
      "Paper 4, Q2",
      "Paper 4, Q1"
    ],
    "GCSE - German (AQA)": [
      "Paper 4, Q2",
      "Paper 4, Q1"
    ],
    // A-Level
    "A-Level - Psychology (AQA)": [
      "Paper 1, Section A-D (16 mark question)"
    ],
    "A-Level - French (AQA)": [
      "Paper 2, Section A (40 mark essay)",
      "Paper 2, Section B (40 Mark Essay)"
    ],
    "A-Level - Spanish (AQA)": [
      "Paper 2, Section A (40 mark essay)",
      "Paper 2, Section B (40 Mark Essay)"
    ],
    "A-Level - English Literature B (AQA)": [
      "25-Mark Question"
    ],
    "A-Level - English Literature A (AQA)": [
      "25-Mark Question"
    ],
    // Fallback option
    "Other": ["Other"]
  });
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    subject: '',
    category: '',
    user_id: '',
    teacher: ''
  });
  
  // Available filter options
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // Assignment form
  const [assignmentForm, setAssignmentForm] = useState({
    qaFeedbackId: null,
    teacherId: '',
    studentName: '',
    studentEmail: '',
    originalGrade: '',
    gradingCategory: '' // For AI grading
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin())) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && user && isAdmin()) {
      loadQAFeedbackEssays();
      loadTrainingAssignments();
      loadTeachers();
      // Categories are now hardcoded in state, no need to load
    }
  }, [user, authToken, activeTab, currentPage, filters, showNonQAOnly, trainingSearch]);

  const isAdmin = () => {
    // Check if user is admin (you may need to adjust this based on your admin logic)
    return user && (user.username === 'charles' || user.username === 'cillian' || user.role === 'admin');
  };

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && (!user || !isAdmin())) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const handleBulkImport = () => {
    if (selectedEssays.length === 0) {
      setError('Please select essays to convert');
      return;
    }
    setShowBulkModal(true);
  };

  const handleBulkSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (!bulkSettings.teacherId) {
        setError('Please select a teacher');
        return;
      }

      // Prepare conversions array
      const conversions = selectedEssays.map(essay => {
        // Only proceed if we have a valid grading category
        const gradingCategory = essay.category;
        if (!gradingCategory || !validateCategory(gradingCategory)) {
          throw new Error(`Essay ${essay.id} has invalid or missing category: "${gradingCategory}". Cannot convert to training without a valid grading category.`);
        }
        
        return {
          qa_feedback_id: essay.id,
          teacher_id: parseInt(bulkSettings.teacherId),
          student_name: essay.user_id || 'Unknown Student',
          student_email: `${essay.user_id}@example.com`,
          original_grade: '0/100',
          grading_category: gradingCategory, // Use the valid category for grading
          training_category: 'Training Essay' // Generic label for organization
        };
      });

      const response = await reviewAPI.bulkConvertToTraining(authToken, conversions);
      
      setSuccess(`Bulk conversion completed: ${response.summary.successful} essays converted and graded automatically, ${response.summary.failed} failed`);
      
      // Reset form and close modal
      setSelectedEssays([]);
      setShowBulkModal(false);
      setBulkSettings({
        teacherId: '',
        useAutoMapping: true
      });
      
      // Reload data
      loadQAFeedbackEssays();
      loadTrainingAssignments();
      
    } catch (error) {
      console.error('Bulk conversion error:', error);
      setError(error.message || 'Failed to convert essays');
    } finally {
      setLoading(false);
    }
  };

  const toggleEssaySelection = (essay) => {
    setSelectedEssays(prev => {
      const isSelected = prev.some(e => e.id === essay.id);
      if (isSelected) {
        return prev.filter(e => e.id !== essay.id);
      } else {
        return [...prev, essay];
      }
    });
  };

  const selectAllEssays = () => {
    if (selectedEssays.length === qaFeedbackEssays.length) {
      setSelectedEssays([]);
    } else {
      setSelectedEssays([...qaFeedbackEssays]);
    }
  };

  const loadQAFeedbackEssays = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await reviewAPI.getQAFeedbackEssays(
        authToken, 
        currentPage, 
        20, 
        filters
      );
      
      setQAFeedbackEssays(response.essays || []);
      setTotalPages(response.totalPages || 1);
      setAvailableSubjects(response.availableSubjects || []);
      setAvailableCategories(response.availableCategories || []);
      
    } catch (error) {
      console.error('Error loading QA feedback essays:', error);
      setError('Failed to load essays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTrainingAssignments = async () => {
    try {
      setLoading(true);
      setError('');

      if (showNonQAOnly) {
        // Fetch all completed training essays (admin sees all) with max page size
        const pageSizeBackend = 50;
        // Fetch ALL training essays across pages
        const first = await reviewAPI.getTrainingEssays(
          authToken,
          1,
          pageSizeBackend,
          filters.subject || null
        );
        let allEssays = first.essays || [];
        const totalPagesServer = first.totalPages || 1;
        if (totalPagesServer > 1) {
          const restPages = Array.from({ length: totalPagesServer - 1 }, (_, i) => i + 2);
          const restResponses = await Promise.all(
            restPages.map(p => reviewAPI.getTrainingEssays(authToken, p, pageSizeBackend, filters.subject || null))
          );
          for (const resp of restResponses) {
            allEssays = allEssays.concat(resp.essays || []);
          }
        }

        // Also fetch ALL QA-derived training assignments across pages to build an exclusion set
        const qaFirst = await reviewAPI.getTrainingAssignments(
          authToken,
          1,
          pageSizeBackend,
          { teacher: '', subject: filters.subject }
        );
        let qaAssignments = qaFirst.assignments || [];
        const qaTotalPages = qaFirst.totalPages || 1;
        if (qaTotalPages > 1) {
          const qaRestPages = Array.from({ length: qaTotalPages - 1 }, (_, i) => i + 2);
          const qaRestResponses = await Promise.all(
            qaRestPages.map(p => reviewAPI.getTrainingAssignments(authToken, p, pageSizeBackend, { teacher: '', subject: filters.subject }))
          );
          for (const resp of qaRestResponses) {
            qaAssignments = qaAssignments.concat(resp.assignments || []);
          }
        }
        const qaIdSet = new Set(qaAssignments.map(a => a.id));

        // Exclude QA-derived by BOTH: explicit flag (when present) and fallback set-difference
        const nonQa = allEssays.filter(e => {
          const explicitNonQa = (typeof e.source_qa_feedback_id !== 'undefined') ? !e.source_qa_feedback_id : true;
          const notInQaSet = !qaIdSet.has(e.id);
          return explicitNonQa && notInQaSet;
        });

        // Enrich with teacher username when possible
        const byId = new Map(teachers.map(t => [t.id, t]));
        const normalized = nonQa.map(e => ({
          ...e,
          assigned_teacher_username: e.assigned_teacher_id ? (byId.get(e.assigned_teacher_id)?.username || null) : null,
          assigned_at: e.assigned_at || e.submitted_at
        }));

        // Set list and computed pagination (20 per page)
        setTrainingAssignments(normalized);
        const computedTotalPages = Math.max(1, Math.ceil(normalized.length / 20));
        setTotalPages(computedTotalPages);
        if (currentPage > computedTotalPages) {
          setCurrentPage(1);
        }
      } else {
        const response = await reviewAPI.getTrainingAssignments(
          authToken,
          currentPage,
          20,
          { teacher: filters.teacher, subject: filters.subject }
        );
        setTrainingAssignments(response.assignments || []);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error('Error loading training assignments:', error);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const response = await reviewAPI.getTeachers(authToken);
      setTeachers(response.teachers || []);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleAssignEssay = (essay) => {
    setAssignmentForm({
      qaFeedbackId: essay.id,
      teacherId: '',
      studentName: essay.user_id || 'Unknown Student',
      studentEmail: `${essay.user_id}@example.com`,
      originalGrade: '0/100',
      gradingCategory: isValidCategory(essay.category) ? essay.category : ''
    });
  };

  const handleSubmitAssignment = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      if (!assignmentForm.qaFeedbackId || !assignmentForm.teacherId) {
        setError('Please select both an essay and a teacher');
        return;
      }
      
      // Validate that we have a valid grading category
      const gradingCategory = assignmentForm.gradingCategory;
      if (!gradingCategory || !validateCategory(gradingCategory)) {
        setError(`Invalid or missing grading category: "${gradingCategory}". Cannot convert to training without a valid grading category.`);
        return;
      }

      const response = await reviewAPI.convertToTraining(
        authToken,
        assignmentForm.qaFeedbackId,
        parseInt(assignmentForm.teacherId),
        assignmentForm.studentName,
        assignmentForm.studentEmail,
        assignmentForm.originalGrade,
        gradingCategory, // Use the valid category for grading
        'Training Essay' // Generic label for organization
      );
      
      let successMessage = '';
      if (response.grading_started) {
        successMessage = 'Essay successfully converted to training data and assigned to teacher. AI grading is now running automatically.';
      } else {
        successMessage = response.message || 'Essay converted and assigned to teacher with automatic AI grading';
      }
      
      // Add category information
      if (response.category_info) {
        successMessage += ` Category: ${response.category_info}`;
      } else if (response.category_used) {
        successMessage += ` Using category: ${response.category_used}`;
      }
      
      setSuccess(successMessage);
      
      if (response.grading_error) {
        setError(`Note: ${response.grading_error}`);
      }
      
      setAssignmentForm({
        qaFeedbackId: null,
        teacherId: '',
        studentName: '',
        studentEmail: '',
        originalGrade: '',
        gradingCategory: ''
      });
      
      // Reload data
      if (activeTab === 'qa-feedback') {
        loadQAFeedbackEssays();
      }
      
    } catch (error) {
      console.error('Error assigning essay:', error);
      setError(error.message || 'Failed to assign essay. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Re-grading handlers (for training data only)
  const handleRegradeTraining = async (trainingId) => {
    try {
      setRegrading(prev => ({ ...prev, [trainingId]: 'regrading' }));
      setError('');
      setSuccess('');
      
      const result = await reviewAPI.regradeTrainingEssay(authToken, trainingId);
      
      if (result.task_id) {
        // Start polling for results
        pollForRegradeResults(result.task_id, trainingId);
      } else {
        // Handle immediate result (fallback)
        setSuccess(`Re-grading completed for training essay #${trainingId}`);
        setRegrading(prev => ({ ...prev, [trainingId]: null }));
        await loadTrainingAssignments();
      }
      
    } catch (error) {
      console.error('Error re-grading:', error);
      setError(`Failed to re-grade training essay #${trainingId}: ${error.message}`);
      setRegrading(prev => ({ ...prev, [trainingId]: null }));
    }
  };

  const pollForRegradeResults = async (taskId, trainingId) => {
    const maxAttempts = 30; // 5 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        const statusResponse = await reviewAPI.getTaskStatus(authToken, taskId);
        
        if (statusResponse.status === 'completed') {
          setSuccess(`Re-grading completed for training essay #${trainingId}`);
          setRegrading(prev => ({ ...prev, [trainingId]: null }));
          await loadTrainingAssignments();
          return;
        }
        
        if (statusResponse.status === 'failed') {
          setError(`Re-grading failed for training essay #${trainingId}. Please try again.`);
          setRegrading(prev => ({ ...prev, [trainingId]: null }));
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setError(`Re-grading is taking longer than expected for training essay #${trainingId}. Please refresh the page.`);
          setRegrading(prev => ({ ...prev, [trainingId]: null }));
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setError(`Failed to check re-grading status for training essay #${trainingId}. Please refresh the page.`);
          setRegrading(prev => ({ ...prev, [trainingId]: null }));
        }
      }
    };
    
    poll();
  };

  const handleBulkRegradeTraining = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      if (selectedForRegrade.length === 0) {
        setError('Please select training essays to re-grade');
        return;
      }
      
      const result = await reviewAPI.bulkRegrade(authToken, selectedForRegrade, []);
      
      setSuccess(`Bulk re-grading completed: ${result.summary.successful} successful, ${result.summary.failed} failed`);
      
      // Clear selections
      setSelectedForRegrade([]);
      
      // Refresh the assignments
      await loadTrainingAssignments();
      
    } catch (error) {
      console.error('Error in bulk re-grading:', error);
      setError(`Bulk re-grading failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleTrainingSelection = (id) => {
    setSelectedForRegrade(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const isValidCategory = (category) => {
    if (!category) return false;
    const allCategories = Object.values(validCategories).flat();
    return allCategories.includes(category);
  };

  const getCategoryStatus = (category) => {
    if (!category) return { status: 'missing', color: '#dc2626', text: 'No Category' };
    if (isValidCategory(category)) return { status: 'valid', color: '#16a34a', text: '✓ Valid' };
    return { status: 'invalid', color: '#f59e0b', text: '⚠ Needs Validation' };
  };

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user || !isAdmin()) {
    return <div className="error">Access denied. Admin privileges required.</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', padding: '20px' }}>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
      
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, marginBottom: '5px' }}>Admin Dashboard</h1>
        <p style={{ margin: 0, color: '#64748b' }}>
          Manage essay assignments for teacher review
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          <button
            onClick={() => {
              setActiveTab('qa-feedback');
              setCurrentPage(1);
            }}
            style={{
              padding: '10px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'qa-feedback' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'qa-feedback' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'qa-feedback' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            QA Feedback Essays
          </button>
          <button
            onClick={() => {
              setActiveTab('assignments');
              setCurrentPage(1);
            }}
            style={{
              padding: '10px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'assignments' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'assignments' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'assignments' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            Training Assignments
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{ 
          background: '#fef2f2', 
          border: '1px solid #fecaca', 
          color: '#dc2626', 
          padding: '12px', 
          borderRadius: '6px', 
          marginBottom: '20px' 
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{ 
          background: '#f0fdf4', 
          border: '1px solid #bbf7d0', 
          color: '#16a34a', 
          padding: '12px', 
          borderRadius: '6px', 
          marginBottom: '20px' 
        }}>
          {success}
        </div>
      )}

      {/* QA Feedback Tab */}
      {activeTab === 'qa-feedback' && (
        <div>
          <h3 style={{ margin: '20px 0', color: '#1f2937' }}>QA Feedback Essays</h3>
          
          {/* Sticky Bulk Actions Bar */}
          <div style={{ 
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            paddingTop: '10px',
            paddingBottom: '10px',
            marginBottom: '20px',
            boxShadow: selectedEssays.length > 0 ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '15px',
              background: selectedEssays.length > 0 ? '#f0fdf4' : '#f8fafc',
              borderRadius: '8px',
              border: selectedEssays.length > 0 ? '2px solid #10b981' : '1px solid #e2e8f0',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedEssays.length === qaFeedbackEssays.length && qaFeedbackEssays.length > 0}
                    onChange={selectAllEssays}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  <span style={{ fontWeight: '500' }}>
                    Select All ({selectedEssays.length} of {qaFeedbackEssays.length} selected)
                  </span>
                </label>
                {selectedEssays.length > 0 && (
                  <span style={{ 
                    fontSize: '13px', 
                    color: '#059669',
                    fontStyle: 'italic'
                  }}>
                    → Ready to convert
                  </span>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleBulkImport}
                  disabled={selectedEssays.length === 0}
                  style={{
                    padding: '8px 16px',
                    background: selectedEssays.length > 0 ? '#059669' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedEssays.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    fontSize: selectedEssays.length > 0 ? '15px' : '14px',
                    transform: selectedEssays.length > 0 ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  🚀 Bulk Convert to Training ({selectedEssays.length})
                </button>
              </div>
            </div>
          </div>

          {/* Additional Floating Action Button (appears when scrolled down) */}
          {selectedEssays.length > 0 && (
            <div style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              zIndex: 1000,
              background: '#059669',
              color: 'white',
              borderRadius: '30px',
              padding: '15px 25px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              animation: 'slideIn 0.3s ease-out, pulse 2s infinite'
            }}
            onClick={handleBulkImport}
            >
              <span style={{ fontSize: '20px' }}>🚀</span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px' }}>
                  Convert {selectedEssays.length} Essay{selectedEssays.length > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  Click to bulk convert to training
                </div>
              </div>
            </div>
          )}

          {/* Selection Reminder Notification */}
          {selectedEssays.length > 0 && (
            <div style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1001,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '10px',
              padding: '12px 20px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'slideIn 0.3s ease-out'
            }}>
              <span style={{ fontSize: '18px' }}>📌</span>
              <span style={{ fontWeight: '500' }}>
                {selectedEssays.length} essay{selectedEssays.length > 1 ? 's' : ''} selected - 
                Ready to convert to training data!
              </span>
            </div>
          )}

          {/* Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '15px', 
            marginBottom: '20px',
            padding: '20px',
            background: '#f9fafb',
            borderRadius: '8px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Subject:</label>
              <select
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Subjects</option>
                {availableSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Category:</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Categories</option>
                {availableCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>User ID:</label>
              <input
                type="text"
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                placeholder="Search by user ID..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Essays List */}
          {loading ? (
            <div className="loading">Loading essays...</div>
          ) : qaFeedbackEssays.length === 0 ? (
            <div className="empty-state">
              <h3>No essays found</h3>
              <p>No QA feedback essays match your current filters.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Select
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#6b7280', 
                          fontWeight: '400',
                          padding: '2px 6px',
                          background: '#f3f4f6',
                          borderRadius: '4px'
                        }}>
                          for conversion
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      ID
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      User
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      Subject
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      Category
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      Question
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {qaFeedbackEssays.map((essay, index) => (
                    <tr key={essay.id} style={{ borderBottom: index < qaFeedbackEssays.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="checkbox"
                          checked={selectedEssays.some(e => e.id === essay.id)}
                          onChange={() => toggleEssaySelection(essay)}
                          style={{ transform: 'scale(1.2)' }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>{essay.id}</td>
                      <td style={{ padding: '12px' }}>{essay.user_id}</td>
                      <td style={{ padding: '12px' }}>{essay.subject || 'N/A'}</td>
                      <td style={{ padding: '12px' }}>{essay.category || 'N/A'}</td>
                      <td style={{ padding: '12px' }}>{essay.question_text?.substring(0, 100)}...</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {essay.already_converted ? (
                            <div style={{
                              background: '#f0fdf4',
                              color: '#16a34a',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              ✓ Assigned to {essay.assigned_teacher}
                            </div>
                          ) : (
                            <button
                              onClick={() => handleAssignEssay(essay)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              Assign to Teacher
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Training Assignments Tab */}
      {activeTab === 'assignments' && (
        <div>
          <h3 style={{ margin: '20px 0', color: '#1f2937' }}>Training Assignments</h3>

          {/* Help text about re-grading */}
          <div style={{ 
            background: '#fef3c7', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            fontSize: '14px',
            color: '#92400e',
            border: '1px solid #fbbf24'
          }}>
            <strong>💡 About Re-grading:</strong> Training essays are automatically graded when converted. 
            Use re-grading if: grading failed initially, the grading system was updated, 
            or a teacher requests fresh AI feedback.
          </div>

          {/* Sticky Bulk Re-grade Bar for Training Only */}
          {trainingAssignments.length > 0 && (
            <div style={{ 
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'white',
              paddingTop: '10px',
              paddingBottom: '10px',
              marginBottom: '20px',
              boxShadow: selectedForRegrade.length > 0 ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '15px',
                background: selectedForRegrade.length > 0 ? '#fef3c7' : '#f8fafc',
                borderRadius: '8px',
                border: selectedForRegrade.length > 0 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedForRegrade.length === trainingAssignments.length && trainingAssignments.length > 0}
                      onChange={() => {
                        if (selectedForRegrade.length === trainingAssignments.length) {
                          setSelectedForRegrade([]);
                        } else {
                          setSelectedForRegrade(trainingAssignments.map(a => a.id));
                        }
                      }}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <span style={{ fontWeight: '500' }}>
                      Select All ({selectedForRegrade.length} of {trainingAssignments.length} selected)
                    </span>
                  </label>
                  {selectedForRegrade.length > 0 && (
                    <span style={{ 
                      fontSize: '13px', 
                      color: '#f59e0b',
                      fontStyle: 'italic'
                    }}>
                      → Ready to re-grade
                    </span>
                  )}
                </div>
                
                <button
                  onClick={handleBulkRegradeTraining}
                  disabled={selectedForRegrade.length === 0}
                  style={{
                    padding: '8px 16px',
                    background: selectedForRegrade.length > 0 ? '#f59e0b' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedForRegrade.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    fontSize: selectedForRegrade.length > 0 ? '15px' : '14px',
                    transform: selectedForRegrade.length > 0 ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  ↻ Bulk Re-grade ({selectedForRegrade.length})
                </button>
              </div>
            </div>
          )}

          {/* Floating Action Button for Bulk Re-grade */}
          {selectedForRegrade.length > 0 && (
            <div style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              zIndex: 1000,
              background: '#f59e0b',
              color: 'white',
              borderRadius: '30px',
              padding: '15px 25px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              animation: 'slideIn 0.3s ease-out, pulse 2s infinite'
            }}
            onClick={handleBulkRegradeTraining}
            >
              <span style={{ fontSize: '20px' }}>↻</span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px' }}>
                  Re-grade {selectedForRegrade.length} Essay{selectedForRegrade.length > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  Click to refresh AI grading
                </div>
              </div>
            </div>
          )}

          {/* Assignment Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '15px', 
            marginBottom: '20px',
            padding: '20px',
            background: '#f9fafb',
            borderRadius: '8px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Teacher:</label>
              <input
                type="text"
                value={filters.teacher}
                onChange={(e) => handleFilterChange('teacher', e.target.value)}
                placeholder="Search by teacher username..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Subject:</label>
              <select
                value={filters.subject}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Subjects</option>
                {Object.keys(trainingSubjects).map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Search:</label>
              <input
                type="text"
                value={trainingSearch}
                onChange={(e) => setTrainingSearch(e.target.value)}
                placeholder="Search student, teacher, category, question..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={showNonQAOnly}
                  onChange={(e) => {
                    setShowNonQAOnly(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                Only show training essays not from QA feedback
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="toggleAllTraining"
                  type="checkbox"
                  checked={showAllTraining}
                  onChange={(e) => setShowAllTraining(e.target.checked)}
                />
                Show source and assignment metadata in list
              </label>
            </div>
          </div>

          {/* Assignments List */}
          {loading ? (
            <div className="loading">Loading assignments...</div>
          ) : trainingAssignments.length === 0 ? (
            <div className="empty-state">
              <h3>No assignments found</h3>
              <p>No training assignments match your current filters.</p>
            </div>
          ) : (
            <div>
              <div style={{ 
                display: 'grid', 
                gap: '15px',
                marginBottom: '20px'
              }}>
                {(function() {
                  const filtered = trainingAssignments.filter(a => {
                    if (!trainingSearch.trim()) return true;
                    const q = trainingSearch.toLowerCase();
                    const hay = [
                      String(a.id),
                      a.student_name || '',
                      a.assigned_teacher_username || '',
                      a.category || '',
                      a.subject || '',
                      a.question || ''
                    ].join(' ').toLowerCase();
                    return hay.includes(q);
                  });
                  const perPage = 20;
                  const start = (currentPage - 1) * perPage;
                  const displayed = filtered.slice(start, start + perPage);
                  return displayed.map(assignment => (
                  <div key={assignment.id} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    background: 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <h3 style={{ margin: 0, fontSize: '18px' }}>
                            Training Essay #{assignment.id}
                          </h3>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedForRegrade.includes(assignment.id)}
                              onChange={() => toggleTrainingSelection(assignment.id)}
                              style={{ transform: 'scale(0.9)' }}
                            />
                            Select for re-grade
                          </label>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '14px', color: '#6b7280' }}>
                          <span><strong>Student:</strong> {assignment.student_name}</span>
                          <span><strong>Subject:</strong> {assignment.subject}</span>
                          <span><strong>Category:</strong> {assignment.category}</span>
                          {showAllTraining && (
                            <>
                              <span><strong>From QA?</strong> {assignment.source_qa_feedback_id ? `Yes (#${assignment.source_qa_feedback_id})` : 'No (direct training)'}</span>
                              <span><strong>Teacher ID:</strong> {assignment.assigned_teacher_id ?? '—'}</span>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
                          <span><strong>Teacher:</strong> {assignment.assigned_teacher_username}</span>
                          <span><strong>Assigned:</strong> {formatDate(assignment.assigned_at)}</span>
                          <span><strong>Status:</strong> 
                            <span style={{ 
                              color: assignment.graded ? '#16a34a' : '#f59e0b',
                              fontWeight: '500',
                              marginLeft: '4px'
                            }}>
                              {assignment.graded ? '✓ Graded' : '⏳ Grading in progress'}
                            </span>
                          </span>
                        </div>
                      </div>
                      
                      {/* Re-assign + Re-grade controls */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <select
                          value={assignment.assigned_teacher_id || ''}
                          onChange={async (e) => {
                            const newId = e.target.value ? parseInt(e.target.value) : null;
                            if (!newId) return;
                            try {
                              setReassigning(prev => ({ ...prev, [assignment.id]: true }));
                              await reviewAPI.reassignTrainingEssay(authToken, assignment.id, newId);
                              await loadTrainingAssignments();
                            } catch (err) {
                              setError(err.message || 'Failed to reassign');
                            } finally {
                              setReassigning(prev => ({ ...prev, [assignment.id]: false }));
                            }
                          }}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        >
                          <option value="">Select teacher...</option>
                          {teachers.map(t => (
                            <option key={t.id} value={t.id}>{t.username}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleRegradeTraining(assignment.id)}
                          disabled={reassigning[assignment.id] || regrading[assignment.id] === 'regrading'}
                          style={{
                            background: reassigning[assignment.id] || regrading[assignment.id] === 'regrading' ? '#9ca3af' : '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: reassigning[assignment.id] || regrading[assignment.id] === 'regrading' ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            minWidth: '100px'
                          }}
                        >
                          {reassigning[assignment.id] ? 'Reassigning...' : (regrading[assignment.id] === 'regrading' ? 'Re-grading...' : '↻ Re-grade')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Answer preview */}
                    {assignment.question && (
                      <div style={{ marginTop: '12px', fontSize: '13px', color: '#4b5563' }}>
                        <strong>Question:</strong> {assignment.question}
                      </div>
                    )}
                  </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '10px', 
          marginTop: '20px',
          alignItems: 'center'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              background: currentPage === 1 ? '#f9fafb' : 'white',
              color: currentPage === 1 ? '#9ca3af' : '#374151',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          
          <span style={{ padding: '8px 16px', color: '#6b7280' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              background: currentPage === totalPages ? '#f9fafb' : 'white',
              color: currentPage === totalPages ? '#9ca3af' : '#374151',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Assignment Modal */}
      {assignmentForm.qaFeedbackId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0' }}>Assign Essay to Teacher</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <strong>Essay #{assignmentForm.qaFeedbackId}</strong>
              <p style={{ margin: '5px 0', color: '#6b7280', fontSize: '14px' }}>
                User: {assignmentForm.qaFeedbackId} | 
                Subject: {assignmentForm.qaFeedbackId} | 
                Category: {assignmentForm.gradingCategory}
              </p>
              <div style={{ 
                margin: '8px 0', 
                padding: '6px 10px', 
                borderRadius: '4px',
                fontSize: '12px',
                background: getCategoryStatus(assignmentForm.gradingCategory).status === 'valid' ? '#f0fdf4' : '#fef3c7',
                color: getCategoryStatus(assignmentForm.gradingCategory).color,
                border: `1px solid ${getCategoryStatus(assignmentForm.gradingCategory).color}30`
              }}>
                <strong>Grading Category Status:</strong> {getCategoryStatus(assignmentForm.gradingCategory).text}
                {getCategoryStatus(assignmentForm.gradingCategory).status !== 'valid' && 
                  ' (will be automatically corrected during conversion)'}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Select Teacher <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                value={assignmentForm.teacherId}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, teacherId: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <option value="">Choose a teacher...</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username} ({teacher.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Student Name
              </label>
              <input
                type="text"
                value={assignmentForm.studentName}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, studentName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Student Email
              </label>
              <input
                type="email"
                value={assignmentForm.studentEmail}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, studentEmail: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Original Grade (e.g., "85/100", "A", "17/20")
              </label>
              <input
                type="text"
                value={assignmentForm.originalGrade}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, originalGrade: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              />
            </div>
            
            {/* Show category selector only if original category is invalid */}
            {assignmentForm.qaFeedbackId && !isValidCategory(assignmentForm.gradingCategory) && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Grading Category <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={assignmentForm.gradingCategory}
                  onChange={(e) => setAssignmentForm(prev => ({ ...prev, gradingCategory: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px'
                  }}
                >
                  <option value="">Choose a grading category...</option>
                  {validCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: '#dc2626', margin: '4px 0 0 0' }}>
                  Original category "{assignmentForm.gradingCategory}" is not valid for grading. Please select a valid category.
                </p>
              </div>
            )}
            

            
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #0ea5e9', 
              borderRadius: '6px', 
              padding: '12px', 
              marginBottom: '30px',
              fontSize: '14px',
              color: '#0c4a6e'
            }}>
              <strong>Note:</strong> The essay will be converted to training data and immediately graded by the AI system using the 
              ({assignmentForm.gradingCategory || 'N/A'}) grading category. The teacher will then be able to review and provide feedback on the AI-graded essay.
              You can re-grade training essays later if needed from the Training Assignments tab.
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAssignmentForm({
                  qaFeedbackId: null,
                  teacherId: '',
                  studentName: '',
                  studentEmail: '',
                  originalGrade: '',
                  gradingCategory: ''
                })}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAssignment}
                disabled={loading || !assignmentForm.qaFeedbackId || !assignmentForm.teacherId || (assignmentForm.qaFeedbackId && !isValidCategory(assignmentForm.gradingCategory) && !assignmentForm.gradingCategory)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: (loading || !assignmentForm.qaFeedbackId || !assignmentForm.teacherId || (assignmentForm.qaFeedbackId && !isValidCategory(assignmentForm.gradingCategory) && !assignmentForm.gradingCategory)) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: (loading || !assignmentForm.qaFeedbackId || !assignmentForm.teacherId || (assignmentForm.qaFeedbackId && !isValidCategory(assignmentForm.gradingCategory) && !assignmentForm.gradingCategory)) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Converting & Starting Grading...' : 'Assign Essay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1f2937' }}>
              Bulk Convert to Training Data
            </h2>
            
            <div style={{ marginBottom: '20px', padding: '12px', background: '#f0f9ff', borderRadius: '6px' }}>
              <strong>Selected Essays:</strong> {selectedEssays.length}
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
                {selectedEssays.map(essay => `#${essay.id}`).join(', ')}
              </div>
            </div>

            {/* Teacher Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Assign to Teacher <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                value={bulkSettings.teacherId}
                onChange={(e) => setBulkSettings(prev => ({ ...prev, teacherId: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <option value="">Choose a teacher...</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username} ({teacher.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Auto-mapping option */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.useAutoMapping}
                  onChange={(e) => setBulkSettings(prev => ({ ...prev, useAutoMapping: e.target.checked }))}
                  style={{ transform: 'scale(1.2)' }}
                />
                <span style={{ fontWeight: '500' }}>
                  Use automatic grading category mapping
                </span>
              </label>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 28px' }}>
                When enabled, the system will automatically select grading categories based on training categories. 
                When disabled, it will use the original essay categories.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkSettings({
                    teacherId: '',
                    useAutoMapping: true
                  });
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSubmit}
                disabled={loading || !bulkSettings.teacherId}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: (loading || !bulkSettings.teacherId) ? '#9ca3af' : '#059669',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: (loading || !bulkSettings.teacherId) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? `Converting ${selectedEssays.length} Essays...` : `Convert ${selectedEssays.length} Essays`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 